import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from './AuthContext';
import { getIdlePeriodsFromStorage, clearIdlePeriodsFromStorage } from '../components/IdleDetection';
import { SYSTEM_SETTINGS_DEFAULTS } from '../constants/systemSettingsDefaults';

// Timer Context
const TimerContext = createContext();

const timerReducer = (state, action) => {
  switch (action.type) {
    case 'SET_ACTIVE_TIMER':
      return { ...state, activeTimer: action.payload };
    case 'SET_ELAPSED_TIME':
      return { ...state, elapsedTime: action.payload };
    case 'SET_PROJECTS':
      return { ...state, projects: action.payload };
    case 'SET_TASKS':
      return { ...state, tasks: action.payload };
    case 'SET_TIME_ENTRIES':
      return { ...state, timeEntries: action.payload };
    default:
      return state;
  }
};

export const TimerProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [state, dispatch] = useReducer(timerReducer, {
    activeTimer: null,
    elapsedTime: 0,
    projects: [],
    tasks: [],
    timeEntries: []
  });

  // Timer interval with daily limit validation
  useEffect(() => {
    let interval = null;
    if (state.activeTimer) {
      interval = setInterval(async () => {
        const now = new Date();
        const startTimeStr = state.activeTimer.start_time;
        const startTime = new Date(startTimeStr.endsWith('Z') ? startTimeStr : startTimeStr + 'Z');
        const elapsed = Math.floor((now - startTime) / 1000);
        dispatch({ type: 'SET_ELAPSED_TIME', payload: elapsed });
        
        // Check daily hours limit every 30 seconds to avoid too many API calls
        if (elapsed % 30 === 0) {
          try {
            const response = await axios.get('/api/time-entries/daily-hours');
            const totalHoursToday = response.data.total_hours;
            const dailyHoursCap = response.data.daily_hours_cap ?? SYSTEM_SETTINGS_DEFAULTS.daily_hours_cap;
            const currentTimerHours = elapsed / 3600;
            const totalAfterTimer = totalHoursToday + currentTimerHours;
            
            // If timer would exceed daily cap, auto-stop (backend saves up to boundary)
            if (totalAfterTimer > dailyHoursCap) {
              const remainingHours = dailyHoursCap - totalHoursToday;
              if (remainingHours <= 0) {
                const timerId = state.activeTimer?.id || 'default';
                const idlePeriods = getIdlePeriodsFromStorage(timerId);
                await axios.post('/api/timer/stop', { 
                  idle_periods: idlePeriods,
                  description: 'Auto-stopped: Daily limit reached'
                });
                dispatch({ type: 'SET_ACTIVE_TIMER', payload: null });
                dispatch({ type: 'SET_ELAPSED_TIME', payload: 0 });
                clearIdlePeriodsFromStorage(timerId);
                toast.error('Timer auto-stopped: Daily time limit reached!');
                await fetchTimeEntries();
              } else if (remainingHours < currentTimerHours) {
                const timerId = state.activeTimer?.id || 'default';
                const idlePeriods = getIdlePeriodsFromStorage(timerId);
                await axios.post('/api/timer/stop', { 
                  idle_periods: idlePeriods,
                  description: 'Auto-stopped: Timer capped at daily limit'
                });
                dispatch({ type: 'SET_ACTIVE_TIMER', payload: null });
                dispatch({ type: 'SET_ELAPSED_TIME', payload: 0 });
                clearIdlePeriodsFromStorage(timerId);
                toast.warning(`Timer auto-stopped: Capped at ${remainingHours.toFixed(1)}h (saved) to stay within daily limit`);
                await fetchTimeEntries();
              }
            }
          } catch (error) {
            console.error('Error checking daily hours limit:', error);
          }
        }
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [state.activeTimer]);

  const fetchActiveTimer = async () => {
    // Don't fetch timer if user is not authenticated (or still on 2FA challenge — no token yet)
    if (!isAuthenticated || !localStorage.getItem('token')) {
      return;
    }

    try {
      const response = await axios.get('/api/timer/active');
      dispatch({ type: 'SET_ACTIVE_TIMER', payload: response.data });
      if (response.data) {
        const now = new Date();
        const startTimeStr = response.data.start_time;
        const startTime = new Date(startTimeStr.endsWith('Z') ? startTimeStr : startTimeStr + 'Z');
        const elapsed = Math.floor((now - startTime) / 1000);
        dispatch({ type: 'SET_ELAPSED_TIME', payload: elapsed });
        
        // Immediately check if this timer should be auto-stopped
        try {
          const dailyResponse = await axios.get('/api/time-entries/daily-hours');
          const totalHoursToday = dailyResponse.data.total_hours;
          const dailyHoursCap = dailyResponse.data.daily_hours_cap ?? SYSTEM_SETTINGS_DEFAULTS.daily_hours_cap;
          const currentTimerHours = elapsed / 3600;
          const totalAfterTimer = totalHoursToday + currentTimerHours;
          
          if (totalAfterTimer > dailyHoursCap) {
            const remainingHours = dailyHoursCap - totalHoursToday;
            if (remainingHours <= 0) {
              const timerId = response.data.id || 'default';
              const idlePeriods = getIdlePeriodsFromStorage(timerId);
              await axios.post('/api/timer/stop', { 
                idle_periods: idlePeriods,
                description: 'Auto-stopped: Daily limit already reached'
              });
              dispatch({ type: 'SET_ACTIVE_TIMER', payload: null });
              dispatch({ type: 'SET_ELAPSED_TIME', payload: 0 });
              clearIdlePeriodsFromStorage(timerId);
              toast.error('Timer auto-stopped: Daily time limit already reached!');
              await fetchTimeEntries();
            }
          }
        } catch (error) {
          console.error('Error checking daily hours on timer fetch:', error);
        }
      }
    } catch (error) {
      // Handle error silently
    }
  };

  const startTimer = async (projectId, description, taskId = null) => {
    try {
      const response = await axios.post(`/api/timer/start?project_id=${projectId}&description=${encodeURIComponent(description)}${taskId ? `&task_id=${taskId}` : ''}`);
      dispatch({ type: 'SET_ACTIVE_TIMER', payload: response.data });
      dispatch({ type: 'SET_ELAPSED_TIME', payload: 0 });
      toast.success('Timer started!');
      await fetchTimeEntries();
      return true;
    } catch (error) {
      toast.error('Failed to start timer');
      return false;
    }
  };

  const stopTimer = async (description = null, idlePeriodsOverride = null) => {
    try {
      const timerId = state.activeTimer?.id || 'default';
      const idlePeriods = idlePeriodsOverride ?? getIdlePeriodsFromStorage(timerId);
      await axios.post('/api/timer/stop', { 
        idle_periods: idlePeriods,
        description: description
      });
      dispatch({ type: 'SET_ACTIVE_TIMER', payload: null });
      dispatch({ type: 'SET_ELAPSED_TIME', payload: 0 });
      toast.success('Timer stopped!');
      clearIdlePeriodsFromStorage(timerId);
      // Refresh time entries immediately after stopping timer
      await fetchTimeEntries();
      return true;
    } catch (error) {
      toast.error('Failed to stop timer');
      return false;
    }
  };

  const fetchProjects = async () => {
    if (!isAuthenticated) return;

    try {
      const response = await axios.get('/api/projects/?page_size=100');
      const { results: projects } = response.data;
      dispatch({ type: 'SET_PROJECTS', payload: projects });
    } catch (error) {
      // Handle error silently
    }
  };

  const fetchTasks = async (projectId = null) => {
    if (!isAuthenticated) return;

    try {
      const url = projectId ? `/api/tasks?project_id=${projectId}` : '/api/tasks';
      const response = await axios.get(url);
      const { results: tasks } = response.data;
      dispatch({ type: 'SET_TASKS', payload: tasks });

    } catch (error) {
      // Handle error silently
    }
  };

  const fetchTimeEntries = async () => {
    if (!isAuthenticated) return;

    try {
      const response = await axios.get('/api/time-entries');
      const { results: entries } = response.data;
      dispatch({ type: 'SET_TIME_ENTRIES', payload: entries });
    } catch (error) {
      // Handle error silently
    }
  };

  // Fetch active timer on mount to persist timer state across navigation
  // Only fetch if user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchActiveTimer();
    } else {
      // Clear timer state when user logs out
      dispatch({ type: 'SET_ACTIVE_TIMER', payload: null });
      dispatch({ type: 'SET_ELAPSED_TIME', payload: 0 });
    }
  }, [isAuthenticated]);

  return (
    <TimerContext.Provider value={{
      ...state,
      fetchActiveTimer,
      startTimer,
      stopTimer,
      fetchProjects,
      fetchTasks,
      fetchTimeEntries
    }}>
      {children}
    </TimerContext.Provider>
  );
};

export const useTimer = () => {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error('useTimer must be used within a TimerProvider');
  }
  return context;
};
